import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../context/authContext';
import { generateMcpApiKey, generateMcpOAuthCode } from '../../services/mcpService';
import {
  Box, Paper, Typography, Button, CircularProgress,
  List, ListItem, ListItemIcon, ListItemText, Alert,
} from '@mui/material';
import {
  SearchOutlined, DirectionsCarOutlined, LocationOnOutlined,
  HandshakeOutlined, AssessmentOutlined, BookOutlined,
} from '@mui/icons-material';
import { PrimaryButton } from '../../components/buttons';
import { TextInput } from '../../components/inputs';
import { apiPost } from '../../services/apiClient';
import { buildApiUrl, API_ENDPOINTS } from '../../config/apiEndpoints';
import { useTranslation } from 'react-i18next';

type OAuthStep = 'loading' | 'needs-login' | 'consent' | 'generating' | 'error';

const MCP_TOOLS = [
  { icon: <SearchOutlined fontSize="small" />,     label: 'search_offers',           desc: 'Search available transport offers with pricing' },
  { icon: <DirectionsCarOutlined fontSize="small" />, label: 'get_vehicles',           desc: 'List your fleet vehicles and GPS positions' },
  { icon: <LocationOnOutlined fontSize="small" />,  label: 'get_vehicle_location',    desc: 'Look up a specific vehicle\'s location' },
  { icon: <HandshakeOutlined fontSize="small" />,   label: 'get_negotiations',        desc: 'View active negotiations and prices' },
  { icon: <HandshakeOutlined fontSize="small" />,   label: 'get_negotiation_detail',   desc: 'Read full negotiation history and messages' },
  { icon: <BookOutlined fontSize="small" />,        label: 'get_bookings',            desc: 'List confirmed transport bookings' },
  { icon: <AssessmentOutlined fontSize="small" />,  label: 'get_stats',               desc: 'Access dashboard statistics' },
];

const SCOPES = [
  'Read-only — no write, modify, or delete operations',
  'No access to email, payment details, or account settings',
];

/**
 * OAuth 2.0 Authorize page — MCP connector consent screen.
 *
 * Supports two OAuth 2.0 flows controlled by the response_type parameter:
 *   - response_type=code  (default, recommended): redirects with a one-time
 *     authorization code that the client exchanges at POST /oauth/token.
 *   - response_type=token (implicit, legacy): redirects with the API key
 *     directly in the URL fragment.
 *
 * Flow:
 *   1. If not logged in → compact login form → consent
 *   2. If logged in     → consent screen immediately
 *   3. User reviews permissions → Approve or Deny
 *   4. Approve → generate code/key → redirect back
 *   5. Deny    → redirect with ?error=access_denied&state=...
 */
const OAuthAuthorizePage: React.FC = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirectUri  = searchParams.get('redirect_uri');
  const state         = searchParams.get('state');
  const responseType  = searchParams.get('response_type') || 'code'; // 'code' or 'token'
  const clientName    = searchParams.get('client_name') || 'an AI assistant';

  const [step,           setStep]           = useState<OAuthStep>('loading');
  const [accountName,    setAccountName]    = useState('');
  const [password,       setPassword]       = useState('');
  const [loginError,     setLoginError]     = useState('');
  const [loginLoading,   setLoginLoading]   = useState(false);
  const [genError,       setGenError]       = useState('');

  // ── redirect helpers ──────────────────────────────────────────

  const redirectWithCode = useCallback((code: string) => {
    if (!redirectUri) return;
    const sep = redirectUri.includes('?') ? '&' : '?';
    window.location.href = `${redirectUri}${sep}code=${encodeURIComponent(code)}${state ? `&state=${encodeURIComponent(state)}` : ''}`;
  }, [redirectUri, state]);

  const redirectWithError = useCallback((error: string) => {
    if (!redirectUri) { setStep('error'); setLoginError(error); return; }
    const sep = redirectUri.includes('?') ? '&' : '?';
    window.location.href = `${redirectUri}${sep}error=${encodeURIComponent(error)}${state ? `&state=${encodeURIComponent(state)}` : ''}`;
  }, [redirectUri, state]);

  // ── determine initial step ────────────────────────────────────

  useEffect(() => {
    if (auth?.isLoading) return;
    if (!redirectUri) { setStep('error'); setLoginError(t('Missing redirect_uri parameter')); return; }
    setStep(auth?.isLoggedIn && auth?.user?.tenant_id ? 'consent' : 'needs-login');
  }, [auth?.isLoading, auth?.isLoggedIn, auth?.user?.tenant_id, redirectUri, t]);

  // ── approve / deny ────────────────────────────────────────────

  const handleApprove = useCallback(async () => {
    const tenantId = auth?.user?.tenant_id;
    if (!tenantId) { redirectWithError('no_tenant'); return; }
    setStep('generating');
    try {
      if (responseType === 'token') {
        // Implicit flow — key goes directly in the redirect URL
        const apiKey = await generateMcpApiKey(tenantId);
        redirectWithCode(apiKey);
      } else {
        // Authorization code flow (default) — one-time code exchanged server-side
        const oauthCode = await generateMcpOAuthCode(tenantId);
        redirectWithCode(oauthCode);
      }
    } catch {
      setGenError(t('Failed to generate API key. Please try again.'));
      setStep('consent');
    }
  }, [auth?.user?.tenant_id, responseType, redirectWithCode, redirectWithError, t]);

  const handleDeny = useCallback(() => redirectWithError('access_denied'), [redirectWithError]);

  // ── login handler ─────────────────────────────────────────────

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountName.trim() || !password) { setLoginError(t('Email and password are required')); return; }
    setLoginLoading(true); setLoginError('');

    try {
      const res = await apiPost(buildApiUrl(API_ENDPOINTS.users.login), { account_name: accountName, password }, { skipAuth: true });
      const { result } = await res.json();
      auth?.handleLogin(accountName, result.jwt);
      const userRes = await apiPost(buildApiUrl(API_ENDPOINTS.users.info), {});
      const userData = await userRes.json();
      if (!userData.result?.tenant_id) { redirectWithError('no_tenant'); return; }
      setStep('consent');
    } catch (err: any) {
      setLoginLoading(false);
      const hint = err?.errorData?.error?.additionalInfo;
      if (hint === 'client.extended.softLockedOutLogin30Minutes') setLoginError(t('Too many attempts. Try again in 30 minutes.'));
      else if (hint === 'client.extended.missingResourceLogin')      setLoginError(t('Incorrect email or password.'));
      else                                                          setLoginError(t('Login failed. Please try again.'));
    }
  };

  // ── render: loading / generating ──────────────────────────────

  if (step === 'loading' || step === 'generating') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100">
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', maxWidth: 400, width: '100%' }}>
          <CircularProgress size={32} />
          <Typography variant="body1" mt={2}>
            {step === 'generating' ? t('Generating authorization...') : t('Loading...')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ── render: error ─────────────────────────────────────────────

  if (step === 'error') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100">
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Typography variant="h6" color="error" gutterBottom>{t('Connection Failed')}</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>{loginError || t('An unexpected error occurred.')}</Typography>
          <Button variant="contained" fullWidth onClick={() => navigate('/login')}>{t('Go to Login')}</Button>
        </Paper>
      </Box>
    );
  }

  // ── render: login ─────────────────────────────────────────────

  if (step === 'needs-login') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100">
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Typography variant="h5" gutterBottom>{t('Sign in to Notea')}</Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {t('Sign in to connect your account with {{client}}.', { client: clientName })}
          </Typography>
          <form onSubmit={handleLogin}>
            <TextInput label={t('Email')}    value={accountName} onChange={e => setAccountName(e.target.value)} fullWidth autoFocus />
            <TextInput label={t('Password')} value={password}     onChange={e => setPassword(e.target.value)}    fullWidth type="password" sx={{ mt: 2 }} />
            {loginError && <Typography variant="body2" color="error" mt={1}>{loginError}</Typography>}
            <PrimaryButton type="submit" fullWidth disabled={loginLoading} loading={loginLoading} sx={{ mt: 3 }}>
              {t('Sign In')}
            </PrimaryButton>
          </form>
        </Paper>
      </Box>
    );
  }

  // ── render: consent ───────────────────────────────────────────

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="grey.100">
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, maxWidth: 520, width: '100%' }}>
        <Typography variant="h5" gutterBottom>{t('Connect to Notea')}</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t('{{client}} would like to access your Notea account.', { client: clientName })}
        </Typography>

        {/* Signed-in as */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" fontWeight={500}>{t('Signed in as')}</Typography>
          <Typography variant="body2" color="primary.main">{auth?.userEmail}</Typography>
          <Box flex={1} />
          <Button size="small" onClick={() => { auth?.handleLogout(); setStep('needs-login'); }}>{t('Switch account')}</Button>
        </Box>

        {/* Permissions */}
        <Typography variant="subtitle2" gutterBottom>{t('This will allow access to:')}</Typography>
        <List dense disablePadding>
          {MCP_TOOLS.map(tool => (
            <ListItem key={tool.label} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>{tool.icon}</ListItemIcon>
              <ListItemText primary={tool.label} secondary={tool.desc}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption' }} />
            </ListItem>
          ))}
        </List>
        <Box sx={{ mt: 2, mb: 3 }}>
          {SCOPES.map((s, i) => (
            <Typography key={i} variant="caption" color="text.secondary" display="block" sx={{ '&::before': { content: '"• "' } }}>
              {s}
            </Typography>
          ))}
        </Box>

        {genError && <Alert severity="error" sx={{ mb: 2 }}>{genError}</Alert>}

        <Typography variant="caption" color="warning.main" display="block" mb={2}>
          {t('A new API key will be generated. Any previous MCP API key for your account will be replaced.')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" color="inherit" onClick={handleDeny} sx={{ flex: 1 }}>{t('Deny')}</Button>
          <PrimaryButton onClick={handleApprove} sx={{ flex: 1 }}>{t('Approve')}</PrimaryButton>
        </Box>

        <Typography variant="caption" color="text.disabled" display="block" mt={2} textAlign="center">
          {t('You can revoke this access at any time from your Notea dashboard settings.')}
        </Typography>
      </Paper>
    </Box>
  );
};

export default OAuthAuthorizePage;
